import { Pause } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatTimecode } from '@/core/time/format';
import { cn } from '@/lib/cn';
import type { RecordingPhase } from '@/state/recording-store';

/** Elapsed time with a live (or paused) mark; the same in the app and the floating window. */
export function RecordingStatus({ phase, elapsed, compact = false }: { readonly phase: RecordingPhase; readonly elapsed: number; readonly compact?: boolean }) {
  const { t } = useTranslation();
  const label =
    phase.status === 'retaking'
      ? t('record.takeLabel', { take: phase.take })
      : ({ recording: t('record.live'), paused: t('record.paused'), countdown: t('record.starting'), saving: t('record.savingShort') }[phase.status as string] ?? '');
  return (
    <span className="flex items-center gap-2.5">
      {/* The live mark means "recording now" and nothing else (DESIGN.md, Live Mark Rule). */}
      {phase.status === 'recording' && <span className="size-2.5 shrink-0 animate-pulse rounded-full bg-danger" aria-hidden />}
      {phase.status === 'paused' && <Pause size={14} className="shrink-0 fill-current text-fg-muted" />}
      <span className={cn('font-semibold tracking-[-0.01em] text-fg tabular', compact ? 'text-[15px]' : 'text-[17px]')}>{formatTimecode(elapsed)}</span>
      {!compact && <span className="sr-only sm:not-sr-only sm:text-[13px] sm:text-fg-muted">{label}</span>}
    </span>
  );
}
